import { useEffect, useState } from 'react'
import {
  Typography, Avatar, Tag, Button, Form, Input, message, Upload,
} from 'antd'
import { CloseOutlined, UserOutlined } from '@ant-design/icons'
import * as userApi from '@/api/users'
import type { ProfileUpdateForm, PasswordChangeForm } from '@/api/users'
import { useAuthStore } from '@/stores/auth'
import '../../styles/aurora-page.css'

const { Title, Text } = Typography

/** 头像上传控件（受控组件：value 为图片 URL，保存资料时随表单提交） */
function AvatarUpload({ value, onChange }: { value?: string; onChange?: (v: string) => void }) {
  const [uploading, setUploading] = useState(false)
  return (
    <div style={{ position: 'relative', width: 112 }}>
      <Upload
        accept="image/png,image/jpeg,image/webp,image/gif"
        showUploadList={false}
        disabled={uploading}
        customRequest={async ({ file, onSuccess, onError }) => {
          try {
            setUploading(true)
            const res = await userApi.uploadAvatar(file as File)
            onChange?.(res.url)
            onSuccess?.(res)
            message.success('头像已上传，点击「保存修改」后生效')
          } catch {
            /* 上传失败已由全局提示 */
            onError?.(new Error('upload failed'))
          } finally {
            setUploading(false)
          }
        }}
      >
        <div style={{
          width: 112,
          height: 112,
          borderRadius: 8,
          overflow: 'hidden',
          position: 'relative',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          cursor: 'pointer',
          border: value ? '1px solid var(--au-border, #e8e4dc)' : '1px dashed var(--au-border, #d9d4c7)',
          background: 'var(--au-slate-50, #faf9f6)',
        }}>
          {value ? (
            <img src={value} alt="头像" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
          ) : (
            <UserOutlined style={{ fontSize: 36, color: '#bfb9ac' }} />
          )}
          {/* 居中上传按钮：有头像时加深色遮罩保证可读性 */}
          <div style={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: value ? 'rgba(0, 0, 0, 0.35)' : 'transparent',
          }}>
            <Button size="small" loading={uploading} style={{ pointerEvents: 'none' }}>
              {value ? '更换图片' : '上传图片'}
            </Button>
          </div>
        </div>
      </Upload>
      {/* 右上角叉号：移除头像恢复默认 */}
      {value ? (
        <span
          title="移除头像（恢复默认）"
          onClick={() => onChange?.('')}
          style={{
            position: 'absolute',
            top: -8,
            right: -8,
            width: 22,
            height: 22,
            borderRadius: '50%',
            background: 'rgba(0, 0, 0, 0.55)',
            color: '#fff',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: 11,
            cursor: 'pointer',
            zIndex: 2,
          }}
        >
          <CloseOutlined />
        </span>
      ) : null}
    </div>
  )
}

const roleLabels: Record<number, string> = {
  1: '管理员',
  2: '开发',
  3: '测试',
  4: '产品',
}

const roleColors: Record<number, string> = {
  1: 'red',
  2: 'blue',
  3: 'green',
  4: 'default',
}

/** 北京时间格式化（与用户管理页保持一致） */
const formatBeijing = (iso?: string | null, fallback = '-') => {
  if (!iso) return fallback
  const d = new Date(iso)
  if (isNaN(d.getTime())) return iso
  const fmt = new Intl.DateTimeFormat('zh-CN', {
    timeZone: 'Asia/Shanghai',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  })
  const parts = fmt.formatToParts(d)
  const map: Record<string, string> = {}
  parts.forEach((p) => { if (p.type !== 'literal') map[p.type] = p.value })
  return `${map.year}-${map.month}-${map.day} ${map.hour}:${map.minute}:${map.second}`
}

/** 个人中心：展示个人信息，支持修改自己的资料与密码 */
export default function Profile() {
  const user = useAuthStore((s) => s.user)
  const setUser = useAuthStore((s) => s.setUser)
  const [profileForm] = Form.useForm()
  const [passwordForm] = Form.useForm()

  /** 表单初始值跟随当前用户 */
  useEffect(() => {
    if (user) {
      profileForm.setFieldsValue({
        nickname: user.nickname,
        email: user.email,
        avatar: user.avatar,
      })
    }
  }, [user, profileForm])

  /** 保存基本资料 */
  const handleSaveProfile = async () => {
    try {
      const values = await profileForm.validateFields()
      const payload: ProfileUpdateForm = {
        nickname: values.nickname,
        email: values.email,
        avatar: values.avatar || '',
      }
      const updated = await userApi.updateMyProfile(payload)
      setUser(updated)
      message.success('资料更新成功')
    } catch {
      /* 校验失败或请求失败已由全局提示 */
    }
  }

  /** 修改密码 */
  const handleChangePassword = async () => {
    try {
      const values = await passwordForm.validateFields()
      const payload: PasswordChangeForm = {
        old_password: values.old_password,
        new_password: values.new_password,
      }
      await userApi.changeMyPassword(payload)
      passwordForm.resetFields()
      message.success('密码修改成功，下次登录请使用新密码')
    } catch {
      /* 校验失败或请求失败已由全局提示 */
    }
  }

  if (!user) return null

  return (
    <div className="aurora-page">
      <div className="page-header">
        <Title level={4} style={{ margin: 0, fontWeight: 600 }}>
          个人中心
        </Title>
      </div>

      <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', alignItems: 'flex-start' }}>
        {/* 左侧：个人信息展示 */}
        <div className="content-card" style={{ width: 320, flexShrink: 0, padding: 24 }}>
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8 }}>
            <Avatar size={72} icon={<UserOutlined />} src={user.avatar || undefined}
              style={{ backgroundColor: 'var(--primary)' }} />
            <div style={{ fontSize: 16, fontWeight: 600, color: 'var(--au-slate-900, var(--text))' }}>
              {user.nickname || user.username}
            </div>
            <Text type="secondary" style={{ fontSize: 12 }}>@{user.username}</Text>
            <Tag color={roleColors[user.role]}>{roleLabels[user.role] || user.role}</Tag>
          </div>

          <div style={{ marginTop: 20, display: 'flex', flexDirection: 'column', gap: 12 }}>
            {[
              { label: '用户名', value: user.username },
              { label: '邮箱', value: user.email },
              { label: '最后登录', value: formatBeijing(user.last_login_time, '从未登录') },
              { label: '创建时间', value: formatBeijing(user.create_time) },
            ].map((item) => (
              <div key={item.label} style={{ display: 'flex', justifyContent: 'space-between', gap: 12 }}>
                <Text type="secondary" style={{ fontSize: 13, flexShrink: 0 }}>{item.label}</Text>
                <Text style={{ fontSize: 13, textAlign: 'right', wordBreak: 'break-all' }}>{item.value}</Text>
              </div>
            ))}
          </div>
        </div>

        {/* 右侧：编辑表单 */}
        <div style={{ flex: 1, minWidth: 320, display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div className="content-card" style={{ padding: 24 }}>
            <div style={{ fontSize: 15, fontWeight: 600, marginBottom: 16, color: 'var(--au-slate-900, var(--text))' }}>
              基本资料
            </div>
            <Form form={profileForm} layout="vertical" style={{ maxWidth: 480 }}>
              <Form.Item name="nickname" label="姓名">
                <Input placeholder="请输入姓名" maxLength={64} />
              </Form.Item>
              <Form.Item name="email" label="邮箱"
                rules={[
                  { required: true, message: '请输入邮箱' },
                  { type: 'email', message: '邮箱格式不正确' },
                ]}>
                <Input placeholder="请输入邮箱" />
              </Form.Item>
              <Form.Item name="avatar" label="头像">
                <AvatarUpload />
              </Form.Item>
              <Button type="primary" onClick={handleSaveProfile}>保存修改</Button>
            </Form>
          </div>

          <div className="content-card" style={{ padding: 24 }}>
            <div style={{ fontSize: 15, fontWeight: 600, marginBottom: 16, color: 'var(--au-slate-900, var(--text))' }}>
              修改密码
            </div>
            <Form form={passwordForm} layout="vertical" style={{ maxWidth: 480 }}>
              <Form.Item name="old_password" label="旧密码"
                rules={[{ required: true, message: '请输入旧密码' }]}>
                <Input.Password placeholder="请输入旧密码" />
              </Form.Item>
              <Form.Item name="new_password" label="新密码"
                rules={[
                  { required: true, message: '请输入新密码' },
                  { min: 6, max: 64, message: '6-64 个字符' },
                ]}>
                <Input.Password placeholder="6-64 个字符" />
              </Form.Item>
              <Form.Item name="confirm_password" label="确认新密码"
                dependencies={['new_password']}
                rules={[
                  { required: true, message: '请再次输入新密码' },
                  ({ getFieldValue }) => ({
                    validator(_, value) {
                      if (!value || getFieldValue('new_password') === value) return Promise.resolve()
                      return Promise.reject(new Error('两次输入的密码不一致'))
                    },
                  }),
                ]}>
                <Input.Password placeholder="请再次输入新密码" />
              </Form.Item>
              <Button type="primary" onClick={handleChangePassword}>修改密码</Button>
            </Form>
          </div>
        </div>
      </div>
    </div>
  )
}
